import { assertCompleteRows } from "./aggregateState";
// ==========================================================================
// lib/money.ts — budget codes, trackers, accounts (Money foundation, 9 Oct 2026)
// ==========================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { useHouseholdId } from "./household";

export type Kind = "income" | "expense" | "transfer";
export type Category = {
  id: string; code: string; name: string; kind: Kind; parent_id: string | null; is_group: boolean;
  tax_label: string | null; monthly_budget_cents: number | null; description: string | null; archived_at: string | null;
};
export type Tracker = { id: string; name: string; colour: string; starts_on: string | null; ends_on: string | null; budget_cents: number | null; notes: string | null; archived_at: string | null };
export type AccountKind = "everyday" | "savings" | "offset" | "credit_card" | "loan" | "broker" | "cash" | "other";
export type Account = {
  id: string; owner_entity_id: string | null; co_owner_entity_id: string | null; name: string; kind: AccountKind; institution: string | null; last4: string | null;
  opening_balance_cents: number | null; opening_date: string | null; notes: string | null; closed_on: string | null;
  // Loans (016): how the loan works. Offsets point at their loan.
  loan_style: "redraw" | "offset" | "plain" | null; credit_limit_cents: number | null; interest_rate_pct: number | null; rate_as_at: string | null;
  repayment_type: "pi" | "io" | null; min_repayment_cents: number | null; offset_for_account_id: string | null;
};

export const KINDS: { key: Kind; label: string }[] = [
  { key: "income", label: "Income" }, { key: "expense", label: "Expense" }, { key: "transfer", label: "Money movement" },
];
export const ACCOUNT_KINDS: { key: AccountKind; label: string }[] = [
  { key: "everyday", label: "Everyday" }, { key: "savings", label: "Savings" }, { key: "offset", label: "Offset" },
  { key: "credit_card", label: "Credit card" }, { key: "loan", label: "Loan" }, { key: "broker", label: "Share trading" },
  { key: "cash", label: "Cash" }, { key: "other", label: "Other" },
];

/** 12345 cents -> "$123.45"; whole dollars drop the cents. */
export function money(cents: number | null | undefined, opts: { cents?: boolean } = {}) {
  if (cents === null || cents === undefined) return "—";
  const v = cents / 100;
  return v.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: opts.cents || v % 1 ? 2 : 0, maximumFractionDigits: 2 });
}
/** "1,234.50" or "$1234" -> 123450 cents; blank -> null. */
export function toCents(s: string): number | null {
  const clean = s.replace(/[$,\s]/g, "");
  if (!clean) return null;
  const n = Number(clean);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}
export const fromCents = (c: number | null) => (c === null || c === undefined ? "" : (c / 100).toFixed(c % 100 ? 2 : 0));

function useInv() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return (k: string) => () => { void qc.invalidateQueries({ queryKey: [k, hid] }); void qc.invalidateQueries({ queryKey: ["audit", hid] }); };
}

export function useCategories() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["categories", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("categories").select("id,code,name,kind,parent_id,is_group,tax_label,monthly_budget_cents,description,archived_at").eq("household_id", hid).order("code");
      if (error) throw error;
      return (data as Category[]).map((c) => ({ ...c, monthly_budget_cents: c.monthly_budget_cents === null ? null : Number(c.monthly_budget_cents) }));
    },
  });
}

export function useSaveCategory() {
  const hid = useHouseholdId(); const inv = useInv();
  return useMutation({
    mutationFn: async ({ id, c }: { id?: string; c: Partial<Category> }) => {
      const { error } = id ? await supabase.from("categories").update(c).eq("id", id) : await supabase.from("categories").insert({ ...c, household_id: hid });
      if (error) throw error;
    },
    onSuccess: inv("categories"),
  });
}

export function useTrackers() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["trackers", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("trackers").select("id,name,colour,starts_on,ends_on,budget_cents,notes,archived_at").eq("household_id", hid).order("created_at");
      if (error) throw error;
      return (data as Tracker[]).map((t) => ({ ...t, budget_cents: t.budget_cents === null ? null : Number(t.budget_cents) }));
    },
  });
}

export function useSaveTracker() {
  const hid = useHouseholdId(); const inv = useInv();
  return useMutation({
    mutationFn: async ({ id, t }: { id?: string; t: Partial<Tracker> }) => {
      const { error } = id ? await supabase.from("trackers").update(t).eq("id", id) : await supabase.from("trackers").insert({ ...t, household_id: hid });
      if (error) throw error;
    },
    onSuccess: inv("trackers"),
  });
}

export function useAccounts() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["accounts", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error, count } = await supabase.from("financial_accounts").select("id,owner_entity_id,co_owner_entity_id,name,kind,institution,last4,opening_balance_cents,opening_date,notes,closed_on,loan_style,credit_limit_cents,interest_rate_pct,rate_as_at,repayment_type,min_repayment_cents,offset_for_account_id", { count: "exact" }).eq("household_id", hid).is("deleted_at", null).order("name");
      if (error) throw error;
      assertCompleteRows({ data, count }, "financial_accounts");
      const n = (v: number | null) => (v === null ? null : Number(v));
      return (data as Account[]).map((a) => ({ ...a, opening_balance_cents: n(a.opening_balance_cents), credit_limit_cents: n(a.credit_limit_cents), interest_rate_pct: n(a.interest_rate_pct), min_repayment_cents: n(a.min_repayment_cents) }));
    },
  });
}

export function useSaveAccount() {
  const hid = useHouseholdId(); const inv = useInv();
  return useMutation({
    mutationFn: async ({ id, a }: { id?: string; a: Partial<Account> }) => {
      const { error } = id ? await supabase.from("financial_accounts").update(a).eq("id", id) : await supabase.from("financial_accounts").insert({ ...a, household_id: hid });
      if (error) throw error;
    },
    onSuccess: inv("accounts"),
  });
}

/** Remove an account added by mistake. The database refuses once it has transactions. */
export function useRemoveAccount() {
  const inv = useInv();
  return useMutation({
    mutationFn: async (id: string) => {
      const { count } = await supabase.from("transactions").select("id", { count: "exact", head: true }).eq("account_id", id).is("deleted_at", null);
      if (count) throw new Error(`It has ${count} transactions, so it can't be removed. Set a closed date instead.`);
      const { error } = await supabase.from("financial_accounts").update({ deleted_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: inv("accounts"),
  });
}
